import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

interface NotFoundProps {
  /** Override the default heading, e.g. "Service not found." */
  heading?: string;
  /** Override the default body copy */
  message?: string;
  /** Override the back-link label and destination */
  backLabel?: string;
  backHref?: string;
}

/**
 * ITL 404 page — used both as the catch-all route and embedded inside
 * detail pages (SingleService, SingleProduct, SingleInsight) when a
 * slug resolves to nothing.
 *
 * Usage as a route:
 *   <Route path="*" element={<NotFound />} />
 *
 * Usage embedded (article not found):
 *   <NotFound
 *     heading="Article not found."
 *     message="This article may have moved or been unpublished."
 *     backLabel="Back to all insights"
 *     backHref="/insights"
 *   />
 */
export default function NotFound({
  heading = "Page not found.",
  message = "The page you're looking for either moved or doesn't exist. Let's get you back on track.",
  backLabel = "Go home",
  backHref = "/",
}: NotFoundProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 pt-24 pb-16">
      <div className="text-center max-w-lg w-full">
        {/* Large serif 404 number */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-[96px] sm:text-[120px] leading-none font-light text-itl-navy"
          aria-hidden
        >
          404
        </motion.p>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-serif text-2xl sm:text-3xl font-light text-itl-navy mt-3 mb-4"
        >
          {heading}
        </motion.h1>

        {/* Body */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="text-gray-400 text-[14px] leading-relaxed mb-10 max-w-sm mx-auto"
        >
          {message}
        </motion.p>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to={backHref}
            className="px-8 py-3 bg-itl-navy text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-itl-blue transition-colors duration-200"
          >
            {backLabel}
          </Link>

          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3 border border-gray-200 text-itl-navy text-[12px] font-semibold uppercase tracking-widest hover:border-itl-navy transition-colors duration-200"
          >
            Go back
          </button>
        </motion.div>

        {/* Help text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="mt-10 text-gray-400 text-[12px]"
        >
          Need help?{" "}
          <Link
            to="/contact"
            className="text-itl-navy underline underline-offset-2 hover:text-itl-blue transition-colors"
          >
            Contact us
          </Link>
        </motion.p>

        {/* Decorative rule */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 mx-auto w-24 h-[3px] bg-itl-navy origin-center"
          aria-hidden
        />
      </div>
    </div>
  );
}
